/**
 * Schema-aware control bindings (path + field selector + transform).
 * Lean port of Shady's controlBindings for Adam Phase 0/1.
 */

import {
  normalizeControlPath,
  type ControlMessage,
} from "./controlRegistry";

export type ControlBindingSelector =
  | { type: "named"; key: string }
  | { type: "index"; key: number };

export type ControlBindingMode =
  | "absolute"
  | "relative"
  | "trigger"
  | "toggle"
  | "step";

export type ControlBindingCurve = "linear" | "exp" | "log" | "smoothstep";

export type ControlBindingSpec = {
  path: string;
  selector?: ControlBindingSelector;
  mode?: ControlBindingMode;
  inputRange?: [number, number];
  outputRange?: [number, number];
  clamp?: boolean;
  invert?: boolean;
  curve?: ControlBindingCurve;
};

export type EventFieldSchema = {
  name: string;
  index: number;
  bindable?: boolean;
  defaultForLearn?: boolean;
  suggestedRange?: [number, number];
};

type EventPathSchema = {
  match: (path: string, tokens: string[]) => boolean;
  fields: EventFieldSchema[];
};

const BINDING_MODES = new Set<ControlBindingMode>([
  "absolute",
  "relative",
  "trigger",
  "toggle",
  "step",
]);
const BINDING_CURVES = new Set<ControlBindingCurve>([
  "linear",
  "exp",
  "log",
  "smoothstep",
]);

const EVENT_SCHEMAS: EventPathSchema[] = [
  {
    match: (_path, tokens) => tokens[tokens.length - 1] === "pitchbend",
    fields: [
      { name: "raw14", index: 0, suggestedRange: [0, 16383] },
      {
        name: "normalized",
        index: 1,
        suggestedRange: [-1, 1],
        defaultForLearn: true,
      },
      {
        name: "channel",
        index: 2,
        bindable: false,
        suggestedRange: [1, 16],
      },
    ],
  },
  {
    match: (_path, tokens) =>
      tokens.length >= 2
      && tokens[tokens.length - 2] === "cc"
      && isIntegerToken(tokens[tokens.length - 1]!),
    fields: [
      {
        name: "value",
        index: 0,
        suggestedRange: [0, 127],
        defaultForLearn: true,
      },
      {
        name: "channel",
        index: 1,
        bindable: false,
        suggestedRange: [1, 16],
      },
    ],
  },
  {
    match: (_path, tokens) =>
      tokens.length >= 2
      && tokens[tokens.length - 2] === "noteon"
      && isIntegerToken(tokens[tokens.length - 1]!),
    fields: [
      {
        name: "velocity",
        index: 0,
        suggestedRange: [0, 127],
        defaultForLearn: true,
      },
      {
        name: "channel",
        index: 1,
        bindable: false,
        suggestedRange: [1, 16],
      },
    ],
  },
];

function isIntegerToken(token: string): boolean {
  return /^\d+$/.test(token);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function normalizeRange(range: [number, number]): [number, number] {
  const a = Number(range[0]);
  const b = Number(range[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) {
    return [0, 1];
  }
  return a <= b ? [a, b] : [b, a];
}

function tokensOf(path: string): string[] {
  return normalizeControlPath(path).split("/").filter(Boolean);
}

export function getBindableFields(
  path: string,
  values: number[],
): Array<EventFieldSchema & { bindable: boolean }> {
  const tokens = tokensOf(path);
  for (const schema of EVENT_SCHEMAS) {
    if (!schema.match(path, tokens)) {
      continue;
    }
    return schema.fields.map(field => ({
      ...field,
      bindable: field.bindable !== false,
    }));
  }
  // Unknown path — expose finite args as index fields; first is default.
  return values.map((_, index) => ({
    name: `arg${index}`,
    index,
    bindable: true,
    defaultForLearn: index === 0,
    suggestedRange: [0, 1] as [number, number],
  }));
}

export function normalizeControlBinding(entry: unknown): ControlBindingSpec | null {
  if (typeof entry === "string") {
    const path = normalizeControlPath(entry);
    return path ? { path } : null;
  }
  if (!entry || typeof entry !== "object") {
    return null;
  }
  const record = entry as Record<string, unknown>;
  const path = normalizeControlPath(
    typeof record.path === "string" ? record.path : "",
  );
  if (!path) {
    return null;
  }

  const selector = parseSelector(record.selector ?? record.valueSelector);
  const mode =
    typeof record.mode === "string" && BINDING_MODES.has(record.mode as ControlBindingMode)
      ? (record.mode as ControlBindingMode)
      : undefined;
  const curve =
    typeof record.curve === "string" && BINDING_CURVES.has(record.curve as ControlBindingCurve)
      ? (record.curve as ControlBindingCurve)
      : undefined;

  const inputRange = parseRange(record.inputRange);
  const outputRange = parseRange(record.outputRange);

  const normalized: ControlBindingSpec = { path };
  if (selector) {
    normalized.selector = selector;
  }
  if (mode) {
    normalized.mode = mode;
  }
  if (inputRange) {
    normalized.inputRange = inputRange;
  }
  if (outputRange) {
    normalized.outputRange = outputRange;
  }
  if (typeof record.clamp === "boolean") {
    normalized.clamp = record.clamp;
  }
  if (typeof record.invert === "boolean") {
    normalized.invert = record.invert;
  }
  if (curve) {
    normalized.curve = curve;
  }
  return normalized;
}

export type LearnCandidate = {
  path: string;
  selector: ControlBindingSelector;
  inputRange: [number, number];
};

/**
 * Pick the most active bindable field from a window of control messages.
 * Prefers schema-backed MIDI paths (CC / pitchbend / noteon).
 */
export function chooseLearnCandidate(events: ControlMessage[]): LearnCandidate | null {
  type Stats = {
    path: string;
    field: EventFieldSchema & { bindable: boolean };
    min: number;
    max: number;
    count: number;
    changes: number;
    last: number;
    scoreBias: number;
    fromSchema: boolean;
  };

  const candidates = new Map<string, Stats>();

  for (const event of events) {
    if (event.source === "ui" || event.source === "custom-ui") {
      continue;
    }
    const values = Array.isArray(event.args) ? event.args : [];
    if (values.length === 0) {
      continue;
    }
    const fields = getBindableFields(event.path, values).filter(f => f.bindable !== false);
    const fromSchema = !fields[0]?.name.startsWith("arg");
    for (const field of fields) {
      const value = values[field.index];
      if (!Number.isFinite(value)) {
        continue;
      }
      const key = `${event.path}::${field.name}`;
      const existing = candidates.get(key);
      if (!existing) {
        candidates.set(key, {
          path: event.path,
          field,
          min: value as number,
          max: value as number,
          count: 1,
          changes: 0,
          last: value as number,
          scoreBias: field.defaultForLearn ? 0.2 : 0,
          fromSchema,
        });
        continue;
      }
      existing.count += 1;
      existing.min = Math.min(existing.min, value as number);
      existing.max = Math.max(existing.max, value as number);
      if (Math.abs(existing.last - (value as number)) > 1e-7) {
        existing.changes += 1;
        existing.last = value as number;
      }
    }
  }

  if (candidates.size === 0) {
    return null;
  }

  const all = Array.from(candidates.values());
  const pool = all.some(c => c.fromSchema) ? all.filter(c => c.fromSchema) : all;
  const ranked = pool
    .map(candidate => {
      const span = candidate.max - candidate.min;
      const suggested = candidate.field.suggestedRange;
      const suggestedSpan = suggested
        ? Math.abs(suggested[1] - suggested[0])
        : null;
      const normalizedSpan =
        suggestedSpan && suggestedSpan > 0 ? span / suggestedSpan : span;
      const activity =
        candidate.count > 1 ? candidate.changes / (candidate.count - 1) : 0;
      return {
        ...candidate,
        score: normalizedSpan + activity * 0.1 + candidate.scoreBias,
      };
    })
    .sort((a, b) => b.score - a.score);

  const winner = ranked[0];
  if (!winner) {
    return null;
  }

  const inputRange = normalizeRange(
    winner.field.suggestedRange ?? [winner.min, winner.max],
  );

  return {
    path: winner.path,
    selector: { type: "named", key: winner.field.name },
    inputRange,
  };
}

export function buildLearnedControlBinding(
  candidate: LearnCandidate,
  outputRange: [number, number],
  options: { mode?: ControlBindingMode } = {},
): ControlBindingSpec {
  return {
    path: candidate.path,
    selector: candidate.selector,
    mode: options.mode ?? "absolute",
    inputRange: normalizeRange(candidate.inputRange),
    outputRange: normalizeRange(outputRange),
    clamp: true,
    invert: false,
    curve: "linear",
  };
}

export type BindingRuntimeState = {
  lastInput: number | null;
  toggled: boolean;
};

export function createBindingRuntimeState(initialToggled = false): BindingRuntimeState {
  return {
    lastInput: null,
    toggled: initialToggled,
  };
}

/**
 * Post-map mode pass (Shady `applyModeScalar`).
 * `mapBindingValue` always produces an absolute continuum; modes interpret that
 * continuum as deltas, edges, or gates against the live param value.
 */
export function applyBindingMode(
  mode: ControlBindingMode | undefined,
  mappedValue: number,
  currentValue: number,
  controlRange: [number, number],
  runtime?: BindingRuntimeState,
): number {
  const modeKind = mode ?? "absolute";
  const [min, max] = controlRange;
  if (modeKind === "relative") {
    return currentValue + mappedValue;
  }
  if (modeKind === "step") {
    const step = (max - min) / 100;
    if (mappedValue > 0.01) {
      return currentValue + step;
    }
    if (mappedValue < -0.01) {
      return currentValue - step;
    }
    return currentValue;
  }
  if (modeKind === "trigger") {
    const threshold = min + (max - min) * 0.5;
    return mappedValue >= threshold ? max : min;
  }
  if (modeKind === "toggle") {
    if (!runtime) {
      return currentValue;
    }
    const threshold = min + (max - min) * 0.5;
    const last = runtime.lastInput;
    if (last !== null && last < threshold && mappedValue >= threshold) {
      runtime.toggled = !runtime.toggled;
    }
    runtime.lastInput = mappedValue;
    return runtime.toggled ? max : min;
  }
  return mappedValue;
}

/**
 * When switching to relative, shrink a full-span absolute outputRange to ±1% bipolar deltas.
 * Leaves custom ranges alone.
 */
export function suggestRelativeOutputRange(
  outputRange: [number, number] | undefined,
  paramRange: [number, number],
): [number, number] {
  const [p0, p1] = normalizeRange(paramRange);
  const span = p1 - p0;
  const delta = Math.max(Math.abs(span) * 0.01, 1e-6);
  const current = normalizeRange(outputRange ?? paramRange);
  const tol = Math.abs(span) * 0.05 + 1e-9;
  const looksAbsolute =
    Math.abs(current[0] - p0) <= tol && Math.abs(current[1] - p1) <= tol;
  if (!looksAbsolute) {
    return current;
  }
  return [-delta, delta];
}

function parseRange(value: unknown): [number, number] | undefined {
  if (!Array.isArray(value) || value.length < 2) {
    return undefined;
  }
  const a = Number(value[0]);
  const b = Number(value[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) {
    return undefined;
  }
  return normalizeRange([a, b]);
}

function parseSelector(value: unknown): ControlBindingSelector | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  if (record.type === "named" && typeof record.key === "string" && record.key.trim()) {
    return { type: "named", key: record.key.trim() };
  }
  if (record.type === "index" && Number.isFinite(record.key)) {
    return { type: "index", key: Math.max(0, Math.round(Number(record.key))) };
  }
  if (record.kind === "named" && typeof record.name === "string" && record.name.trim()) {
    return { type: "named", key: record.name.trim() };
  }
  if (record.kind === "index" && Number.isFinite(record.index)) {
    return { type: "index", key: Math.max(0, Math.round(Number(record.index))) };
  }
  return undefined;
}

/** Pick the numeric field this binding cares about from a message. */
export function selectBindingValue(
  binding: ControlBindingSpec,
  message: ControlMessage,
): number | null {
  const values = Array.isArray(message.args) ? message.args : [];
  if (values.length === 0) {
    return null;
  }

  const selector = binding.selector;
  if (!selector) {
    const fields = getBindableFields(message.path, values).filter(field => field.bindable !== false);
    const defaultField = fields.find(field => field.defaultForLearn) ?? fields[0];
    if (!defaultField) {
      return null;
    }
    const value = values[defaultField.index];
    return Number.isFinite(value) ? (value as number) : null;
  }

  if (selector.type === "index") {
    const index = Math.max(0, Math.round(selector.key));
    const value = values[index];
    return Number.isFinite(value) ? (value as number) : null;
  }

  const field = getBindableFields(message.path, values).find(
    entry => entry.name === selector.key,
  );
  if (!field) {
    return null;
  }
  const value = values[field.index];
  return Number.isFinite(value) ? (value as number) : null;
}

/** Map a selected controller value into a parameter's output range. */
export function mapBindingValue(
  value: number,
  binding: ControlBindingSpec,
  fallbackOutputRange: [number, number],
): number {
  const inputRange = normalizeRange(
    binding.inputRange ?? inferInputRange(binding, value),
  );
  const outputRange = normalizeRange(binding.outputRange ?? fallbackOutputRange);
  const inputSpan = inputRange[1] - inputRange[0];
  let normalized = inputSpan === 0 ? 0 : (value - inputRange[0]) / inputSpan;

  if (binding.clamp ?? true) {
    normalized = clamp(normalized, 0, 1);
  }
  if (binding.invert) {
    normalized = 1 - normalized;
  }

  const curve = binding.curve ?? "linear";
  if (curve === "exp") {
    normalized = normalized * normalized;
  } else if (curve === "log") {
    normalized = Math.sqrt(Math.max(0, normalized));
  } else if (curve === "smoothstep") {
    normalized = normalized * normalized * (3 - 2 * normalized);
  }

  return outputRange[0] + normalized * (outputRange[1] - outputRange[0]);
}

function inferInputRange(
  binding: ControlBindingSpec,
  value: number,
): [number, number] {
  const fields = getBindableFields(binding.path, [value]);
  const selector = binding.selector;
  if (selector?.type === "named") {
    const field = fields.find(entry => entry.name === selector.key);
    if (field?.suggestedRange) {
      return field.suggestedRange;
    }
  }
  const defaultField = fields.find(field => field.defaultForLearn) ?? fields[0];
  return defaultField?.suggestedRange ?? [0, 1];
}

export function describeControlBinding(spec: ControlBindingSpec): string {
  const selector = spec.selector;
  if (!selector) {
    return spec.path;
  }
  if (selector.type === "named") {
    return `${spec.path} • ${selector.key}`;
  }
  return `${spec.path} • value[${selector.key}]`;
}

export type ControlBindingFieldOption = {
  label: string;
  selector: ControlBindingSelector;
  suggestedRange?: [number, number];
};

/** Field choices for the binding editor (Source path → Field dropdown). */
export function getControlBindingFieldOptions(
  path: string,
  valueCount = 4,
): ControlBindingFieldOption[] {
  const probe = Array.from({ length: Math.max(1, valueCount) }, (_, i) => i);
  return getBindableFields(path, probe)
    .filter(field => field.bindable !== false)
    .map(field => ({
      label: field.name,
      selector: { type: "named" as const, key: field.name },
      suggestedRange: field.suggestedRange,
    }));
}

export function controlBindingSpecKey(spec: ControlBindingSpec): string {
  const selector = spec.selector
    ? spec.selector.type === "named"
      ? `named:${spec.selector.key}`
      : `index:${spec.selector.key}`
    : "-";
  const inputRange = spec.inputRange
    ? `${spec.inputRange[0]}:${spec.inputRange[1]}`
    : "-";
  const outputRange = spec.outputRange
    ? `${spec.outputRange[0]}:${spec.outputRange[1]}`
    : "-";
  return [
    normalizeControlPath(spec.path),
    selector,
    spec.mode ?? "absolute",
    inputRange,
    outputRange,
    spec.clamp === false ? "0" : "1",
    spec.invert ? "1" : "0",
    spec.curve ?? "linear",
  ].join("|");
}
